import { jsPDF } from 'jspdf'
import { toPng } from 'html-to-image'

/**
 * Deep clone an element with all computed styles preserved
 * @param {HTMLElement} element - The element to clone
 * @returns {HTMLElement} - The cloned element with styles
 */
function cloneElementWithStyles(element) {
    const clone = element.cloneNode(false)
    const computedStyle = window.getComputedStyle(element)

    // Copy all computed styles as inline styles
    for (let i = 0; i < computedStyle.length; i++) {
        const prop = computedStyle[i]
        clone.style[prop] = computedStyle.getPropertyValue(prop)
    }

    // Handle canvas elements specially
    if (element.tagName === 'CANVAS') {
        const canvas = element
        const clonedCanvas = clone
        clonedCanvas.width = canvas.width
        clonedCanvas.height = canvas.height
        const ctx = clonedCanvas.getContext('2d')
        ctx.drawImage(canvas, 0, 0)
    }

    // Recursively clone children
    for (let i = 0; i < element.childNodes.length; i++) {
        const child = element.childNodes[i]
        if (child.nodeType === Node.ELEMENT_NODE) {
            clone.appendChild(cloneElementWithStyles(child))
        } else {
            clone.appendChild(child.cloneNode(true))
        }
    }

    return clone
}

/**
 * Composable for exporting multiple DOM elements as a multi-page PDF
 */
export function usePdfExport() {
    /**
     * Generates a PDF from a list of DOM elements
     * @param {Array<HTMLElement>} sections - Array of DOM elements to capture
     * @param {string} filename - The filename for the downloaded PDF
     * @param {Object} options - Optional configuration
     * @param {string} options.title - Optional title to display on first page
     * @param {Function} options.onProgress - Optional callback for progress updates (current, total)
     * @returns {Promise<void>}
     */
    const generatePdfFromSections = async (sections, filename = 'report', options = {}) => {
        const { title, onProgress } = options
        if (!sections || sections.length === 0) {
            console.error('No sections provided for PDF export')
            return
        }

        try {
            // A4 dimensions in points (pt) at 72 DPI
            // 595.28 x 841.89
            const pageWidth = 595.28
            const pageHeight = 841.89
            const margin = 40
            const contentWidth = pageWidth - (margin * 2)

            const doc = new jsPDF({
                orientation: 'p',
                unit: 'pt',
                format: 'a4',
            })

            let currentY = margin
            let isFirstPage = true

            // Add title if provided
            if (title) {
                doc.setFontSize(20)
                doc.setTextColor(30, 41, 59) // slate-800
                doc.text(title, margin, currentY)
                currentY += 40 // Space after title
            }

            // Create a sandbox container that matches PDF content dimensions exactly
            // Convert PDF points to pixels (assuming 96 DPI)
            const pxPerPt = 96 / 72
            const sandboxWidth = Math.floor(contentWidth * pxPerPt) // Convert PDF points to pixels

            const sandbox = document.createElement('div')
            sandbox.style.cssText = `
                position: absolute;
                left: -9999px;
                top: 0;
                width: ${sandboxWidth}px;
                background: white;
                visibility: hidden;
                pointer-events: none;
            `
            document.body.appendChild(sandbox)

            // Wait for fonts to be ready
            await document.fonts.ready

            for (let i = 0; i < sections.length; i++) {
                // Report progress
                if (onProgress) {
                    onProgress(i + 1, sections.length)
                }
                const element = sections[i]
                if (!element) continue

                // Clone the element with all computed styles preserved
                const clone = cloneElementWithStyles(element)

                // Apply styles to match PDF dimensions exactly
                // Use padding-box sizing so padding is inside the width
                const elementPadding = 20 // Reasonable padding for content
                clone.style.width = `${sandboxWidth}px`
                clone.style.maxWidth = `${sandboxWidth}px`
                clone.style.minWidth = `${sandboxWidth}px`
                clone.style.padding = `${elementPadding}px`
                clone.style.boxSizing = 'border-box'
                clone.style.margin = '0'
                clone.style.overflow = 'visible'
                clone.style.backgroundColor = '#ffffff'
                clone.style.display = 'block'

                sandbox.appendChild(clone)

                // Wait for images and fonts to load
                const images = clone.querySelectorAll('img')
                await Promise.all(
                    Array.from(images).map(img => {
                        if (img.complete) return Promise.resolve()
                        return new Promise(resolve => {
                            img.onload = resolve
                            img.onerror = resolve
                            // Timeout after 2 seconds
                            setTimeout(resolve, 2000)
                        })
                    })
                )

                // Small delay to ensure rendering is complete
                await new Promise(resolve => setTimeout(resolve, 150))

                // Get the actual rendered height of the clone
                const cloneHeight = clone.offsetHeight

                // Capture the clone at exact dimensions (no pixel ratio to avoid scaling issues)
                const dataUrl = await toPng(clone, {
                    cacheBust: true,
                    pixelRatio: 1, // Use 1:1 ratio since we're sizing to exact PDF dimensions
                    backgroundColor: '#ffffff',
                    width: sandboxWidth,
                    height: cloneHeight,
                    filter: (node) => {
                        if (node.dataset && node.dataset.html2canvasIgnore !== undefined) {
                            return false
                        }
                        if (node.classList && node.classList.contains('export-ignore')) {
                            return false
                        }
                        return true
                    },
                })

                // Remove clone from sandbox
                sandbox.removeChild(clone)

                // Convert pixel dimensions to PDF points
                const imgHeightInPt = cloneHeight / pxPerPt

                // Check if we need a new page
                if (currentY + imgHeightInPt > pageHeight - margin) {
                    doc.addPage()
                    currentY = margin
                }

                // Add image at exact dimensions - no scaling
                doc.addImage(dataUrl, 'PNG', margin, currentY, contentWidth, imgHeightInPt)

                // Update Y position for next element, adding some spacing
                currentY += imgHeightInPt + 20
            }

            // Clean up sandbox
            document.body.removeChild(sandbox)

            doc.save(`${filename}.pdf`)
        } catch (error) {
            console.error('Failed to generate PDF:', error)
            throw error
        }
    }

    return {
        generatePdfFromSections,
    }
}

